import * as THREE from 'three';
import { gsap } from 'gsap';
import lottie from 'lottie-web';

export class DonutScene {
    constructor(app) {
        this.app = app;
        this.scene = new THREE.Scene();
        this.donutModel = null;
        this.character = null;
        
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        
        this.isHovering = false;
        this.characterVisible = false;
        
        this.setupScene();
    }

    async init() {
        await this.createRoom();
        await this.loadDonut();
        this.createCharacter();
        this.setupInteractions();
        
        this.app.sceneManager.addScene('donut', this.scene);
    }

    setupScene() {
        // Use transparent background to show the CSS background
        this.scene.background = null;
        
        // Disable fog for debugging visibility issues
        // this.scene.fog = new THREE.Fog(0x000428, 20, 60);
        
        // Add strong lighting to ensure visibility
        this.setupLighting();
    }
    
    setupLighting() {
        // Add strong ambient light for visibility
        this.ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
        this.scene.add(this.ambientLight);
        
        // Add bright directional light
        this.directionalLight = new THREE.DirectionalLight(0xffffff, 2.0);
        this.directionalLight.position.set(-2, 5, 2);
        this.directionalLight.castShadow = false; // Disable shadows for debugging
        this.scene.add(this.directionalLight);
        
        console.log('DonutScene lighting setup complete');
    }

    async createRoom() {
        // Add table and gallery images as 3D elements
        await this.addSceneElements();
        
        // Add garden atmosphere elements
        this.addGardenDetails();
    }

    async addSceneElements() {
        // Add table to the scene
        await this.createTable();
        this.animateSceneElements();
    }

    async createTable() {
        const textureLoader = new THREE.TextureLoader();
        
        try {
            const tableTexture = await new Promise((resolve, reject) => {
                textureLoader.load(
                    '/Table.png',
                    resolve,
                    undefined,
                    reject
                );
            });
            
            const tableGeometry = new THREE.PlaneGeometry(4, 3);
            const tableMaterial = new THREE.MeshStandardMaterial({
                map: tableTexture,
                transparent: true,
                alphaTest: 0.1
            });
            
            this.table = new THREE.Mesh(tableGeometry, tableMaterial);
            this.table.position.set(-4.33, -1.2, -3);
            this.table.rotation.y = Math.PI / 6;
            
            this.scene.add(this.table);
            console.log('Table added to scene');
            
        } catch (error) {
            console.error('Failed to load table texture:', error);
        }
    }

    animateSceneElements() {
        // Table remains stationary - no animation
    }

    addGardenDetails() {
        // Keep it minimal - just floating particles for subtle atmosphere
        this.createGardenParticles();
    }

    createGardenParticles() {
        const particleGeometry = new THREE.BufferGeometry();
        const particleCount = 50;
        const positions = new Float32Array(particleCount * 3);
        
        for (let i = 0; i < particleCount; i++) {
            positions[i * 3] = (Math.random() - 0.5) * 20;
            positions[i * 3 + 1] = Math.random() * 10;
            positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
        }
        
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        
        const particleMaterial = new THREE.PointsMaterial({
            color: 0xffffff,
            size: 0.03,
            transparent: true,
            opacity: 0.7
        });
        
        this.particles = new THREE.Points(particleGeometry, particleMaterial);
        this.scene.add(this.particles);
    }

    async loadDonut() {
        if (!this.app.assets.donut) {
            console.error('Donut asset not loaded');
            return;
        }

        const donutGLTF = this.app.assets.donut;
        this.donutModel = donutGLTF.scene.clone();

        // Apply responsive scaling
        const scaleFactor = this.app.deviceDetector.getResponsiveScaleFactor();
        const baseScale = 0.8;
        this.donutModel.scale.setScalar(baseScale * scaleFactor);
        this.donutModel.position.set(-4, 0.5, -3);
        
        // Ensure donut is visible
        this.donutModel.visible = true;
        
        this.donutModel.traverse((child) => {
            if (child.isMesh) {
                child.visible = true; // Ensure all mesh parts are visible
                child.castShadow = true;
                child.receiveShadow = false;
                
                if (child.material) {
                    // Make materials look more natural
                    child.material.roughness = 0.7;
                    child.material.metalness = 0;
                    child.material.transparent = false;
                    child.material.opacity = 1.0;
                    
                    // Ensure proper material type for better lighting
                    if (child.material.isMeshStandardMaterial) {
                        child.material.envMapIntensity = 0.5;
                    }
                }
            }
        });
        
        // Remove the weird glow effect entirely for now
        // this.donutGlow = null;
        
        this.scene.add(this.donutModel);
        
        // Remove the test cube as it blocks the view
        
        console.log('Donut loaded and added to scene:', {
            visible: this.donutModel.visible,
            position: this.donutModel.position,
            scale: this.donutModel.scale,
            children: this.donutModel.children.length
        });
        
        console.log('Donut scene setup complete');
        
        this.animateDonut();
    }

    createCharacter() {
        console.log('Creating Lottie character...');
        
        // Create a container div for the Lottie animation positioned near gallery
        this.characterContainer = document.createElement('div');
        this.characterContainer.id = 'lottie-character';
        this.characterContainer.style.cssText = `
            position: fixed;
            width: 200px;
            height: 200px;
            top: 100px;
            right: 150px;
            z-index: 15;
            opacity: 0;
            transform: scale(0);
            pointer-events: none;
        `;
        
        document.body.appendChild(this.characterContainer);
        console.log('Character container added to DOM');
        
        // Load the Lottie animation
        try {
            this.lottieAnimation = lottie.loadAnimation({
                container: this.characterContainer,
                renderer: 'svg',
                loop: false,
                autoplay: false,
                path: './animated-guy.json'
            });
            
            console.log('Lottie animation loading...', this.lottieAnimation);
            
            // Add error handling for Lottie loading
            this.lottieAnimation.addEventListener('DOMLoaded', () => {
                console.log('Lottie character loaded successfully');
                
                // Get total frames and calculate half-way point
                this.totalFrames = this.lottieAnimation.totalFrames;
                this.halfFrames = Math.floor(this.totalFrames / 2);
                console.log(`Animation loaded: ${this.totalFrames} total frames, will play first ${this.halfFrames} frames`);
            });
            
            this.lottieAnimation.addEventListener('error', (error) => {
                console.error('Error loading Lottie character:', error);
            });
            
        } catch (error) {
            console.error('Failed to create Lottie animation:', error);
        }
        
        this.characterVisible = false;
    }

    animateDonut() {
        if (this.donutModel) {
            gsap.to(this.donutModel.rotation, {
                y: Math.PI * 2,
                duration: 8,
                ease: "none",
                repeat: -1
            });
            
            gsap.to(this.donutModel.position, {
                y: 0.3,
                duration: 2,
                ease: "power2.inOut",
                yoyo: true,
                repeat: -1
            });
        }
        
        // Remove glow animation since we removed the glow
    }

    setupInteractions() {
        this.app.canvas.addEventListener('mousemove', this.onMouseMove.bind(this));
    }

    onMouseMove(event) {
        // Don't handle mouse move when in gallery mode
        if (this.app.isInGallery()) return;

        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.app.camera);
        
        if (this.donutModel) {
            const intersects = this.raycaster.intersectObject(this.donutModel, true);
            
            if (intersects.length > 0 && !this.isHovering) {
                this.onDonutHover(true);
            } else if (intersects.length === 0 && this.isHovering) {
                this.onDonutHover(false);
            }
        }
    }

    onDonutHover(isHovering) {
        this.isHovering = isHovering;
        
        if (this.donutModel) {
            gsap.to(this.donutModel.scale, {
                x: isHovering ? 0.9 : 0.8,
                y: isHovering ? 0.9 : 0.8,
                z: isHovering ? 0.9 : 0.8,
                duration: 0.3,
                ease: "power2.out"
            });
            
            gsap.to(this.donutModel.rotation, {
                y: this.donutModel.rotation.y + (isHovering ? Math.PI * 0.5 : 0),
                duration: 0.3,
                ease: "power2.out"
            });
        }
        
        this.app.canvas.style.cursor = isHovering ? 'pointer' : 'default';
    }

    checkDonutClick(event) {
        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
        
        this.raycaster.setFromCamera(this.mouse, this.app.camera);
        
        if (this.donutModel) {
            const intersects = this.raycaster.intersectObject(this.donutModel, true);
            return intersects.length > 0 && !this.characterVisible;
        }
        
        return false;
    }

    triggerCharacterPopOut() {
        if (this.characterVisible) return;
        
        console.log('Triggering character pop-out from gallery area');
        this.characterVisible = true;
        
        // Hide the "click/tap donut" text, click indicator, and mouse indicator
        const heroSubtitles = document.querySelectorAll('.hero-subtitle');
        const clickIndicator = document.querySelector('.click-indicator');
        heroSubtitles.forEach(subtitle => subtitle.style.display = 'none');
        if (clickIndicator) clickIndicator.style.display = 'none';
        
        if (this.characterContainer && this.lottieAnimation && this.halfFrames) {
            // Show the character container with animation from gallery area
            this.characterContainer.style.opacity = '1';
            this.characterContainer.style.transform = 'scale(1)';
            
            // Play only the first half of the animation
            this.lottieAnimation.goToAndStop(0, true); // Go to first frame
            this.lottieAnimation.playSegments([0, this.halfFrames], true); // Play first half
            
            console.log(`Playing animation from frame 0 to ${this.halfFrames}`);
            
            // Add a bounce effect to the container
            setTimeout(() => {
                this.characterContainer.style.transform = 'scale(1.1)';
                setTimeout(() => {
                    this.characterContainer.style.transform = 'scale(1)';
                }, 200);
            }, 100);
            
            // Start dialogue sequence after animation completes
            this.lottieAnimation.addEventListener('complete', () => {
                console.log('Half animation completed - starting dialogue');
                this.startDialogueSequence();
            });
        } else {
            console.log('Cannot trigger animation - missing elements:', {
                container: !!this.characterContainer,
                animation: !!this.lottieAnimation,
                halfFrames: this.halfFrames
            });
        }
    }

    startDialogueSequence() {
        const speechBubble = document.getElementById('character-speech-bubble');
        const dialogueText = document.getElementById('character-dialogue-text');

        if (!speechBubble || !dialogueText) return;

        const messages = [
            "Hey there!",
            "That donut is for you!",
            "In exchange you have to check out my art gallery.",
            "I just updated it with new pieces, let me know what you think!",
        ];

        let currentMessage = 0;
        this.dialogueTimeouts = []; // Store timeouts so we can clear them

        const showNextMessage = () => {
            if (currentMessage < messages.length && !this.dialogueStopped) {
                dialogueText.textContent = messages[currentMessage];
                speechBubble.classList.add('show');

                const timeout1 = setTimeout(() => {
                    if (this.dialogueStopped) return;
                    speechBubble.classList.remove('show');
                    const timeout2 = setTimeout(() => {
                        if (this.dialogueStopped) return;
                        currentMessage++;
                        if (currentMessage < messages.length) {
                            showNextMessage();
                        } else {
                            // Dialogue complete - transition to gallery
                            console.log('Dialogue completed - transitioning to gallery');
                            const timeout3 = setTimeout(() => {
                                if (!this.dialogueStopped) {
                                    this.app.transitionToGallery();
                                }
                            }, 1000); // Wait 1 second after last message
                            this.dialogueTimeouts.push(timeout3);
                        }
                    }, 300);
                    this.dialogueTimeouts.push(timeout2);
                }, 2500); // Show each message for 2.5 seconds
                this.dialogueTimeouts.push(timeout1);
            }
        };

        this.dialogueStopped = false;
        showNextMessage();
    }

    stopDialogue() {
        this.dialogueStopped = true;
        // Clear all pending timeouts
        if (this.dialogueTimeouts) {
            this.dialogueTimeouts.forEach(timeout => clearTimeout(timeout));
            this.dialogueTimeouts = [];
        }
        // Hide speech bubble immediately
        const speechBubble = document.getElementById('character-speech-bubble');
        if (speechBubble) {
            speechBubble.classList.remove('show');
            speechBubble.style.opacity = '0';
        }
    }


    update() {
        // Animate garden particles
        if (this.particles) {
            this.particles.rotation.y += 0.001;
            
            const positions = this.particles.geometry.attributes.position.array;
            for (let i = 1; i < positions.length; i += 3) {
                positions[i] += Math.sin(Date.now() * 0.001 + i) * 0.001;
            }
            this.particles.geometry.attributes.position.needsUpdate = true;
        }
    }

    onResize() {
        // Update donut scaling based on new screen size
        if (this.donutModel) {
            const scaleFactor = this.app.deviceDetector.getResponsiveScaleFactor();
            const baseScale = 0.8;
            this.donutModel.scale.setScalar(baseScale * scaleFactor);
        }
    }

    dispose() {
        // Clean up Lottie animation
        if (this.lottieAnimation) {
            this.lottieAnimation.destroy();
        }
        if (this.characterContainer && this.characterContainer.parentNode) {
            this.characterContainer.parentNode.removeChild(this.characterContainer);
        }
        
        
        // Clean up Three.js objects
        this.scene.traverse((object) => {
            if (object.geometry) object.geometry.dispose();
            if (object.material) {
                if (Array.isArray(object.material)) {
                    object.material.forEach(material => material.dispose());
                } else {
                    object.material.dispose();
                }
            }
        });
    }
}